/**
 * Shared utility functions for Library components
 */

/**
 * Find a resource by slug from the resources array
 * @param {Object} item - Resource item with slug
 * @param {Array} resources - Full resources array
 * @returns {Object|undefined} Matched resource or undefined
 */
function findResourceBySlug(item, resources) {
  if (!resources?.length || !item?.slug) return undefined;
  return resources.find(r => r.slug === item.slug);
}

/**
 * Get player type from item or fallback to resources array
 * @param {Object} item - Resource item
 * @param {Array} resources - Full resources array for fallback lookup
 * @returns {string|null} Player type
 */
export function getPlayerType(item, resources = []) {
  if (item?.playerType) {
    return item.playerType;
  }
  const resource = findResourceBySlug(item, resources);
  if (resource) {
    return resource.playerType || resource.playerSettings?.playerType || null;
  }
  return null;
}

/**
 * Get type icon src based on player type
 * @param {Object} item - Resource item
 * @param {Array} resources - Full resources array for fallback
 * @returns {string} Icon path
 */
export function getTypeIconSrc(item, resources) {
  const playerType = getPlayerType(item, resources);
  if (playerType === 'embedYoutube' || playerType === 'nativeVideo') {
    return '/assets/icons/video-icon.svg';
  }
  return '/assets/icons/headphone.svg';
}

/**
 * Get duration from item with fallback to resources array
 * @param {Object} item - Resource item
 * @param {Array} resources - Full resources array for fallback
 * @param {Function} formatDuration - Duration formatter function
 * @returns {string} Formatted duration
 */
export function getDuration(item, resources, formatDuration) {
  const rawDuration =
    item?.duration ?? item?.durationSeconds ?? item?.durationInSeconds ?? item?.durationInSec;
  const formatted = formatDuration(rawDuration);
  if (formatted) {
    return formatted;
  }
  const resource = findResourceBySlug(item, resources);
  if (resource) {
    const fallbackDuration =
      resource.duration ??
      resource.durationSeconds ??
      resource.durationInSeconds ??
      resource.durationInSec;
    return formatDuration(fallbackDuration);
  }
  return '';
}

/**
 * Extract animated thumbnail from resource
 * @param {Object} resource - Resource object
 * @returns {string|null} Animated thumbnail URL
 */
export function getAnimatedThumbnail(resource) {
  return resource?.animatedThumbnailUrl || null;
}

/**
 * Extract fallback thumbnail from resource
 * @param {Object} resource - Resource object
 * @returns {string} Thumbnail URL
 */
export function getThumbnail(resource) {
  return resource?.thumbnail || '';
}

/**
 * Transform pastor names to string
 * @param {Array} pastors - Array of pastor names
 * @returns {string} Comma-separated names or single name
 */
export function transformPastorNames(pastors) {
  if (!pastors) return '';
  return pastors.length > 1 ? pastors.join(', ') : pastors[0];
}

/**
 * Format a sermon date to "Month Year" (e.g. "March 2026")
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted month and year, or empty string if invalid
 */
export function formatSermonDate(date) {
  if (!date) return '';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Get formatted sermon date from item with fallback to resources array
 * @param {Object} item - Resource item
 * @param {Array} resources - Full resources array for fallback lookup
 * @returns {string} Formatted month and year, or empty string
 */
export function getSermonDate(item, resources = []) {
  const date = item?.date;
  if (date) return formatSermonDate(date);
  const resource = findResourceBySlug(item, resources);
  if (resource?.date) return formatSermonDate(resource.date);
  return '';
}
